import { Icon } from '@iconify/react';
import React, { useEffect, useRef, useState } from "react";
import { getAllAssistant } from "../utils/api/assistantAPI";
import FieldTooltip from './child/FieldTooltip';
import { motion } from 'framer-motion';

const WidgetGenerator = () => {
    const [assistants, setAssistants] = useState([]);
    const [selectedAssistantId, setSelectedAssistantId] = useState("");
    const [companyName, setCompanyName] = useState("Your Company Name");
    const [buttonColor, setButtonColor] = useState("#237247");
    const [position, setPosition] = useState("right");
    const [agentName, setAgentName] = useState("");
    const iframeRef = useRef(null);
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedScript);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const injectScriptToIframe = () => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(`
  <!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Widget Preview</title>
    <style>
      * {
        box-sizing: border-box;
      }
      body {
        margin: 0;
        padding: 0;
        height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        text-align: center;
        font-family: sans-serif;
      }
      #widget-preview-root {
        margin-bottom: 20px;
      }
    </style>
  </head>
  <body>
    <div id="widget-preview-root"></div>
    <div>Your Web Page</div>

    <script
      src="https://cdn-callhq-widget.s3.ap-south-1.amazonaws.com/loader.js"
      data-agent-id="${selectedAssistantId}"
      data-assistant-name="${agentName}"
      data-company-name="${companyName}"
      data-button-color="${buttonColor}"
      data-position="${position}"
      defer
    ></script>
  </body>
  </html>
`);

        doc.close();
    };

    useEffect(() => {
        if (selectedAssistantId) injectScriptToIframe();
    }, [selectedAssistantId, agentName, companyName, buttonColor, position]);

    useEffect(() => {
        const fetchData = async () => {
            const res = await getAllAssistant();
            const list = res.assistants || [];
            setAssistants(list);
            if (list.length > 0) {
                setSelectedAssistantId(list[0].externalAssistantId);
            }
        };
        fetchData();
    }, []);

    const generatedScript = `<script
  src="https://cdn-callhq-widget.s3.ap-south-1.amazonaws.com/loader.js"
  data-agent-id="${selectedAssistantId}"
  data-assistant-name="${agentName}"
  data-company-name="${companyName}"
  data-button-color="${buttonColor}"
  data-position="${position}"
  defer
></script>`;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="card h-100 p-24 radius-12"
        >
            <div className="card-body p-0">
                <div className="row gy-4">
                    <div className="col-md-6">
                        {/* Assistant Dropdown */}
                        <label className="form-label fw-medium">Assistant</label>
                        <FieldTooltip content="Select the AI assistant you want this widget to connect with. Each assistant handles calls differently based on how it’s trained." />
                        <div className="position-relative">
                            <Icon icon="lucide:bot" width="18" height="18" style={iconStyle} />
                            <select
                                className="form-control"
                                value={selectedAssistantId}
                                onChange={(e) => setSelectedAssistantId(e.target.value)}
                                style={selectStyle}
                            >
                                <option value="">Select Assistant</option>
                                {assistants.map((assistant) => (
                                    <option key={assistant.id} value={assistant.externalAssistantId}>
                                        {assistant.name}
                                    </option>
                                ))}
                            </select>
                            <Icon icon="lucide:chevron-down" width="18" height="18" style={dropdownIconStyle} />
                        </div>

                        {/* Company Name */}
                        <div className="form-group mt-3">
                            <label className="form-label fw-medium">Company Name</label>
                            <FieldTooltip content="Your company’s name will be shown on the widget. This helps users know who they’re speaking with." />
                            <input
                                className="form-control"
                                type="text"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                            />
                        </div>

                        {/* Agent Name */}
                        <div className="form-group mt-3">
                            <label className="form-label fw-medium">Agent Name</label>
                            <FieldTooltip content="Display name of the AI agent shown to users. You can personalize it (e.g., Priya, Rohan, SupportBot)." />
                            <input
                                className="form-control"
                                type="text"
                                placeholder="Enter Agent Name e.g. Riya"
                                value={agentName}
                                onChange={(e) => setAgentName(e.target.value)}
                            />
                        </div>

                        {/* Button Color */}
                        <div className="form-group mt-3">
                            <label className="form-label fw-medium">Button Color</label>
                            <FieldTooltip content="Choose the color for the widget’s chat button. Match your brand or website theme." />
                            <input
                                type="color"
                                className="form-control"
                                style={{ height: "45px", padding: "0.375rem 0.75rem" }}
                                value={buttonColor}
                                onChange={(e) => setButtonColor(e.target.value)}
                            />
                        </div>

                        {/* Position Dropdown */}
                        <label className="form-label fw-medium mt-3">Widget Position</label>
                        <FieldTooltip content="Select where the widget appears on your website screen — bottom-left or bottom-right corner." />
                        <div className="position-relative">
                            <Icon icon="lucide:layout" width="18" height="18" style={iconStyle} />
                            <select
                                className="form-control"
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                style={selectStyle}
                            >
                                <option value="right">Right</option>
                                <option value="left">Left</option>
                            </select>
                            <Icon icon="lucide:chevron-down" width="18" height="18" style={dropdownIconStyle} />
                        </div>

                        {/* Script Section with Copy */}
                        <div className="d-flex justify-content-between align-items-center mt-4 w-full align-items-center"
                        >
                            <label
                                style={{
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                    marginTop: "16px",
                                }}
                            >
                                Script
                            </label>
                            <button
                                className='flex align-items-center'
                                onClick={handleCopy}
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#e2e2e2")}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#f1f1f1")}
                                style={{
                                    border: "1px solid black",
                                    padding: "5px 10px",
                                    fontSize: "14px",
                                    borderRadius: "6px",
                                    backgroundColor: "#f1f1f1",
                                }}
                            >
                                <Icon
                                    icon={copied ? "mdi:check-bold" : "material-symbols:content-copy"}
                                    style={{ marginRight: "4px" }}
                                />
                                {copied ? "Copied!" : "Copy"}
                            </button>
                        </div>


                        <pre
                            style={{
                                backgroundColor: "#1e1e1e",
                                color: "#d4d4d4",           
                                fontFamily: "'Fira Code', 'Consolas', 'Menlo', 'Monaco', monospace",
                                fontSize: "14px",
                                lineHeight: "1.6",
                                padding: "16px",
                                borderRadius: "8px",
                                overflowX: "auto",
                                whiteSpace: "pre-wrap",
                                minHeight: "160px",
                                boxShadow: "inset 0 0 0 1px #333",
                            }}
                        >
                            <code>{generatedScript}</code>
                        </pre>

                    </div>

                    {/* Live Preview Section */}
                    <div className="col-md-6">
                        <div className="mt-4">
                            <label className="form-label fw-medium">Live Demo</label>
                            <iframe 
                                ref={iframeRef}
                                style={{
                                    width: "100%",
                                    height: "75.7vh",
                                    border: "1px solid #ccc",
                                    borderRadius: "8px",
                                    
                                }}
                                title="Widget Live Preview"
                                scrolling="auto"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// Styles
const iconStyle = {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6c757d"
};

const selectStyle = {
    paddingLeft: "40px",
    height: "45px",
    appearance: "none",
    borderColor: "#e1e5eb",
    fontSize: "14px"
};

const dropdownIconStyle = {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: "#6c757d"
};

export default WidgetGenerator;
